// Surviving a bust (DESIGN §6): stand crews down on half pay, mothball idle rigs, see the daily burn and each
// well's break-even oil price, and get warned when a price collapse starts eating the cash.
import { eqDef, fieldDef, unitPrice } from './defs.js';
import { crewWage, removeFromAssignments } from './crew.js';
import { netback, opexPerWell } from './production.js';
import { rigSafety } from './safety.js';
import { m4ReactivateGuard } from './m4/marketRigs.js'; // M4-B: cold-stacked > 2 years → 45 days & 15% of the new price
import { spotPrice } from '../economy/market.js';
import { notify, fmt$ } from './util.js';
import { dayToDate } from '../core/time.js';

export const STANDBY_PAY = 0.5;          // retainer as a share of the daily wage
export const REACTIVATE_DAYS = 5;        // bringing a mothballed rig back into service
const MAINT_RATE = 0.02 / 365;           // matches finance.js rig maintenance
const ALERT_COOLDOWN = 120;

// ---- crew on standby -------------------------------------------------------------------
export function standDownImpl(state, crewId) {
  const c = state.crew[crewId];
  if (!c || c.status !== 'active') return { ok: false, msg: 'No such crew member.' };
  if (c.standby) return { ok: false, msg: `${c.name} is already on standby.` };
  removeFromAssignments(state, c);
  c.standby = { sinceDay: state.day };
  return { ok: true, msg: `${c.name} stood down on half pay. The skills keep, but morale sags the longer the wait.` };
}

export function recallImpl(state, crewId) {
  const c = state.crew[crewId];
  if (!c || c.status !== 'active') return { ok: false, msg: 'No such crew member.' };
  if (!c.standby) return { ok: false, msg: `${c.name} isn't on standby.` };
  c.standby = null;
  c.morale = Math.min(100, (c.morale ?? 50) + 5);
  return { ok: true, msg: `${c.name} is back on full pay and ready to work.` };
}

// ---- mothballed rigs ---------------------------------------------------------------------
export function mothballImpl(state, rigUid) {
  const rig = state.rigs[rigUid];
  if (!rig) return { ok: false, msg: 'No such rig.' };
  if (rig.mothballed) return { ok: false, msg: `${rig.name} is already in storage.` };
  if (rig.wellId) return { ok: false, msg: `Release ${rig.name} from its well first.` };
  if (rig.charterOut) return { ok: false, msg: `${rig.name} is out on charter until the job ends.` };
  if (state.contracts && Object.values(state.contracts.active ?? {}).some((c) => c.rigUid === rig.uid)) return { ok: false, msg: `${rig.name} is on a drilling contract.` };
  for (const id of [...(rig.crewIds ?? [])]) { const c = state.crew[id]; if (c) removeFromAssignments(state, c); }
  rig.crewIds = [];
  rig.mothballed = { sinceDay: state.day };
  rig.readyDay = null;
  return { ok: true, msg: `${rig.name} greased, tarped and put in storage. No more upkeep until you bring it back (${REACTIVATE_DAYS} days).` };
}

export function reactivateImpl(state, rigUid) {
  const rig = state.rigs[rigUid];
  if (!rig) return { ok: false, msg: 'No such rig.' };
  if (!rig.mothballed) return { ok: false, msg: `${rig.name} isn't in storage.` };
  const r4 = m4ReactivateGuard(state, rig); // M4-B (null before 2000 / short stacks)
  if (r4) return r4;
  rig.mothballed = null;
  rig.readyDay = state.day + REACTIVATE_DAYS;
  return { ok: true, msg: `${rig.name} is coming out of storage — ready in ${REACTIVATE_DAYS} days.` };
}

// Guard used by assignRig: a rig in storage or still being reactivated can't take a well.
export function rigUnavailable(state, rig) {
  if (rig.mothballed) return `${rig.name} is in storage. Reactivate it in the Fleet panel first.`;
  if (rig.readyDay && rig.readyDay > state.day) return `${rig.name} is being brought out of storage — ready in ${rig.readyDay - state.day} days.`;
  return null;
}

// ---- daily burn & break-even ------------------------------------------------------------
const RUNNING_CATS = ['insurance', 'logistics', 'security', 'protection', 'charter', 'storage', 'licenses']; // not refining: its product sales aren't in this view's revenue either
export function burnView(state) {
  const crew = Object.values(state.crew).filter((c) => c.status === 'active');
  let wages = 0, standby = 0, idleWages = 0;
  const idleCrew = [];
  for (const c of crew) {
    const w = crewWage(state, c);
    if (c.standby) { standby += w * STANDBY_PAY; continue; }
    const pay = c.injuredUntil && c.injuredUntil > state.day ? w * 0.5 : w;
    wages += pay;
    const rig = c.rigId ? state.rigs[c.rigId] : null;
    const busy = (rig && rig.wellId) || c.siteId || c.labUid;
    if (!busy) { idleWages += pay; idleCrew.push(c.id); }
  }
  let maintenance = 0, mothballSavings = 0, idleRigUpkeep = 0;
  const idleRigs = [];
  for (const r of Object.values(state.rigs)) {
    const m = unitPrice(state, eqDef(r.defId)) * MAINT_RATE;
    if (r.mothballed) mothballSavings += m; else { maintenance += m; if (!r.wellId && !(r.readyDay > state.day)) { idleRigs.push(r.uid); idleRigUpkeep += m; } }
  }
  let safety = 0;
  for (const r of Object.values(state.rigs)) safety += rigSafety(r).budget ?? 0;
  let research = 0;
  for (const p of Object.values(state.research?.projects ?? {})) research += p.perDay ?? p.burn ?? 0;
  let interest = 0;
  for (const l of state.company.loans ?? []) interest += l.principal * l.ratePct / 100 / 365;

  const year = dayToDate(state.day).year;
  const opex = opexPerWell(year);
  const spot = spotPrice(state);
  const wells = [];
  let opexTotal = 0, revenue = 0;
  const nbByField = new Map(); // netback(state, fid) is pure here (nothing changes during the view): once per field
  for (const w of Object.values(state.wells)) {
    if (w.status !== 'PRODUCING' || !w.prod || w.ownerOperatorId) continue;
    const shut = !!w.prod.shutIn;
    const rate = shut ? 0 : w.prod.rate ?? 0;
    let nb = nbByField.get(w.fieldId);
    if (!nb) nbByField.set(w.fieldId, (nb = netback(state, w.fieldId)));
    const take = nb.spot > 0 ? (nb.gross - nb.royalty) / nb.spot : 0.875;   // share of spot you keep after quality & royalty
    const perBblCost = nb.transport + nb.region + nb.barrel;
    const lastRate = shut ? (w.prod.rate || w.prod.qi || 0) : (w.prod.rate ?? 0); // a shut-in well shows what it would make; an open one what it makes (0 with no host)
    const breakeven = lastRate > 0 && take > 0 ? (opex / lastRate + perBblCost) / take : Infinity;
    const margin = rate * nb.net - (shut ? 0 : opex);
    if (!shut) { opexTotal += opex; revenue += rate * Math.max(0, nb.net); }
    wells.push({ id: w.id, name: w.name, fieldId: w.fieldId, field: fieldDef(w.fieldId)?.name ?? w.fieldId, shutIn: shut,
      rate: lastRate, netPerBbl: nb.net, opex, marginPerDay: margin, breakeven, losing: !shut && margin < 0 });
  }
  wells.sort((a, b) => a.marginPerDay - b.marginPerDay);
  // Running costs the per-well model doesn't see (platform opex, insurance, boats & helicopters, refineries, guards,
  // charters, storage…): last month's books, per day. Well upkeep takes whichever is larger, the model or the books.
  const last = state.finance?.history?.at(-1), perDay = (k) => (last?.[k] ?? 0) / 30.4;
  const bookedOpex = Math.max(0, perDay('opex') - opexTotal);
  const other = RUNNING_CATS.reduce((a, k) => a + perDay(k), 0);
  const costs = { wages, standby, maintenance, opex: opexTotal + bookedOpex, research, safety, interest, other };
  const fixed = Object.values(costs).reduce((a, b) => a + b, 0);
  const net = revenue - fixed;
  return {
    spot, costs, fixed, revenue, net,
    runwayDays: net < 0 ? Math.floor(Math.max(0, state.company.cash) / -net) : null,
    idleWages, idleCrew, idleRigs, idleRigUpkeep, mothballSavings, wells,
    standbyCount: crew.filter((c) => c.standby).length,
    mothballedCount: Object.values(state.rigs).filter((r) => r.mothballed).length,
  };
}

// ---- crash alert (weekly) ----------------------------------------------------------------
export function tickBurnAlert(state) {
  if (state.day % 7 !== 0) return;
  const a = (state.company.burnAlert ??= { lastDay: -9999 });
  if (state.day - a.lastDay < ALERT_COOLDOWN) return;
  const b = burnView(state);
  if (b.net >= 0 || b.fixed < 1) return;
  const hist = state.market?.history ?? [];
  const then = hist.find(([d]) => d >= state.day - 91)?.[1];
  const crashed = then > 0 && b.spot < 0.6 * then;
  // Only when cash is actually at risk: a crash with under a year of runway, or under 60 days regardless.
  const runway = b.runwayDays ?? Infinity;
  if (!(crashed && runway < 365) && !(runway < 60)) return;
  a.lastDay = state.day;
  const why = crashed ? `Crude has collapsed to ${fmt$(b.spot)}/bbl` : 'Your costs are outrunning your oil';
  const text = `${why}. You're spending ${fmt$(b.fixed)}/day and taking in ${fmt$(b.revenue)}/day` +
    (b.runwayDays != null ? ` — about ${b.runwayDays} days of cash left.` : '.') + ' Review costs in Finance.';
  notify(state, 'cashBurn', { pause: true, text, runwayDays: b.runwayDays, level: 'warn' });
}
